import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { DirectoryUser, UserDirectoryApiService } from '../../../shared/services/user-directory-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { WhatsappAssignmentApiService } from '../services/whatsapp-assignment-api.service';
import {
  WhatsappAssignmentRole,
  WhatsappPhoneResponse,
  WHATSAPP_ASSIGNMENT_ROLE_LABELS,
  whatsappLabel,
} from '../models/whatsapp.model';

@Component({
  selector: 'app-whatsapp-assignment-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent],
  templateUrl: './whatsapp-assignment-modal.component.html',
})
export class WhatsappAssignmentModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() phone: WhatsappPhoneResponse | null = null;
  @Output() close = new EventEmitter<void>();

  private readonly userDirectory = inject(UserDirectoryApiService);
  private readonly authService = inject(AuthService);
  private readonly assignmentApi = inject(WhatsappAssignmentApiService);

  loadingUsers = false;
  saving = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  searchTerm = '';
  users: DirectoryUser[] = [];
  selectedRole: WhatsappAssignmentRole = 'AGENT';

  roleLabel(role: string | null | undefined): string {
    return whatsappLabel(WHATSAPP_ASSIGNMENT_ROLE_LABELS, role);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen) {
      this.errorMessage = null;
      this.successMessage = null;
      this.searchTerm = '';
      this.selectedRole = 'AGENT';
      this.loadUsers();
    }
  }

  get filteredUsers(): DirectoryUser[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.users;
    return this.users.filter(
      (u) =>
        (u.fullname || '').toLowerCase().includes(term) ||
        (u.lastname || '').toLowerCase().includes(term) ||
        (u.mail || '').toLowerCase().includes(term)
    );
  }

  loadUsers(): void {
    // Directorio de la empresa: solo usuarios ACTIVOS (igual que el `isActive: true` anterior),
    // negocio resuelto desde el token y sin exigir `users:VIEW` — con el endpoint anterior un
    // agente sin ese permiso no podía asignar a nadie porque la lista llegaba en 403.
    this.loadingUsers = true;
    this.errorMessage = null;
    this.userDirectory.searchUsers('', 200).subscribe({
      next: (users) => {
        this.users = users;
        this.loadingUsers = false;
      },
      error: (err) => {
        this.errorMessage = this.toErrorMessage(err, 'No se pudieron cargar los usuarios');
        this.loadingUsers = false;
      },
    });
  }

  assign(user: DirectoryUser): void {
    if (!this.phone) return;
    this.saving = true;
    this.errorMessage = null;
    this.successMessage = null;
    this.assignmentApi
      .assign({
        userId: user.id,
        whatsappPhoneId: this.phone.id,
        role: this.selectedRole,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.successMessage = `${user.fullname || user.mail} asignado como ${this.selectedRole}`;
        },
        error: (err) => {
          this.saving = false;
          this.errorMessage = this.toErrorMessage(err, 'No se pudo asignar el usuario');
        },
      });
  }

  closeModal(): void {
    this.close.emit();
  }

  private toErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const body: any = err.error;
      if (typeof body === 'string') return body;
      if (body?.message) return body.message;
      if (body?.error) return body.error;
      return `${err.status} ${err.statusText || fallback}`;
    }
    return fallback;
  }
}
